import { useState, useRef, useCallback, useEffect } from "react";

const STORAGE_KEY = "hourglass_timer";

interface TimerState {
  /** Seconds accumulated from prior start/stop cycles */
  accumulatedSeconds: number;
  /** Timestamp (ms) when timer was last started, or null if paused */
  startedAtMs: number | null;
}

const DEFAULT_STATE: TimerState = { accumulatedSeconds: 0, startedAtMs: null };

function isValidTimerState(v: unknown): v is TimerState {
  if (typeof v !== "object" || v === null) return false;
  const s = v as Record<string, unknown>;
  return (
    typeof s.accumulatedSeconds === "number" &&
    s.accumulatedSeconds >= 0 &&
    (s.startedAtMs === null ||
      (typeof s.startedAtMs === "number" && s.startedAtMs > 0 && s.startedAtMs <= Date.now()))
  );
}

function loadTimerState(): TimerState {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return DEFAULT_STATE;
    const parsed: unknown = JSON.parse(raw);
    if (!isValidTimerState(parsed)) return DEFAULT_STATE;
    return parsed;
  } catch {
    return DEFAULT_STATE;
  }
}

function saveTimerState(state: TimerState) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  } catch {
    // Ignore storage errors — timer still works in-memory
  }
}

function computeElapsed(state: TimerState): number {
  if (state.startedAtMs === null) return Math.floor(state.accumulatedSeconds);
  return Math.max(
    0,
    Math.floor(
      state.accumulatedSeconds + (Date.now() - state.startedAtMs) / 1000
    )
  );
}

interface UseTimerOptions {
  /**
   * Called when the timer is stopped (not reset).
   * Receives the elapsed seconds at the moment of stopping.
   */
  onStop?: (elapsedSeconds: number) => void;
}

interface UseTimerReturn {
  /** Current elapsed time in seconds */
  elapsedSeconds: number;
  /** Whether the timer is currently running */
  isRunning: boolean;
  /** Start the timer (no-op if already running) */
  start: () => void;
  /** Stop the timer and trigger onStop callback */
  stop: () => void;
  /** Reset the timer to 0 without triggering onStop */
  reset: () => void;
}

/**
 * Custom hook for managing a timer with start/stop/reset functionality.
 * Timer state is persisted to localStorage via a start timestamp,
 * so it stays accurate across page reloads.
 */
export function useTimer({ onStop }: UseTimerOptions = {}): UseTimerReturn {
  const stateRef = useRef<TimerState>(null!);
  if (stateRef.current === null) {
    stateRef.current = loadTimerState();
  }
  const [elapsedSeconds, setElapsedSeconds] = useState(() =>
    computeElapsed(stateRef.current)
  );
  const [isRunning, setIsRunning] = useState(
    () => stateRef.current.startedAtMs !== null
  );
  const intervalRef = useRef<number | null>(null);

  const clearTickInterval = useCallback(() => {
    if (intervalRef.current !== null) {
      window.clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  }, []);

  // Snap elapsed time to correct value when returning to a backgrounded tab,
  // since browsers throttle/suspend setInterval in hidden tabs.
  useEffect(() => {
    const onVisible = () => {
      if (document.visibilityState === "visible" && stateRef.current.startedAtMs !== null) {
        setElapsedSeconds(computeElapsed(stateRef.current));
      }
    };
    document.addEventListener("visibilitychange", onVisible);
    return () => document.removeEventListener("visibilitychange", onVisible);
  }, []);

  // Tick every second to update the display while running
  useEffect(() => {
    if (stateRef.current.startedAtMs !== null) {
      intervalRef.current = window.setInterval(() => {
        setElapsedSeconds(computeElapsed(stateRef.current));
      }, 1000);
    }
    return () => clearTickInterval();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const start = useCallback(() => {
    if (stateRef.current.startedAtMs !== null) return;

    stateRef.current = {
      ...stateRef.current,
      startedAtMs: Date.now(),
    };
    saveTimerState(stateRef.current);
    setIsRunning(true);

    clearTickInterval();
    intervalRef.current = window.setInterval(() => {
      setElapsedSeconds(computeElapsed(stateRef.current));
    }, 1000);
  }, [clearTickInterval]);

  const stop = useCallback(() => {
    if (stateRef.current.startedAtMs === null) return;

    clearTickInterval();
    const elapsed = computeElapsed(stateRef.current);

    stateRef.current = {
      accumulatedSeconds: elapsed,
      startedAtMs: null,
    };
    saveTimerState(stateRef.current);
    setIsRunning(false);
    setElapsedSeconds(elapsed);

    onStop?.(elapsed);
  }, [clearTickInterval, onStop]);

  const reset = useCallback(() => {
    clearTickInterval();
    stateRef.current = DEFAULT_STATE;
    try { localStorage.removeItem(STORAGE_KEY); } catch { /* ignore */ }
    setIsRunning(false);
    setElapsedSeconds(0);
  }, [clearTickInterval]);

  return {
    elapsedSeconds,
    isRunning,
    start,
    stop,
    reset,
  };
}
